// eslint-disable-next-line @github-ui/github-monorepo/prefer-github-ui-react-router
import type {ScrollRestorationProps as ScrollRestorationPropsReactRouter} from 'react-router'
import {
  useIsTanStackRouter,
  Outlet as OutletTanStack,
  Link as LinkTanStack,
  NavLink as NavLinkTanStack,
} from './tanstack-router'
import {
  Outlet as OutletReactRouter,
  SoftNavOnlyLink as SoftNavOnlyLinkReactRouter,
  SoftNavOnlyNavLink as SoftNavOnlyNavLinkReactRouter,
  Link as LinkReactRouter,
  NavLink as NavLinkReactRouter,
  type LinkProps as LinkPropsReactRouter,
  type NavLinkProps as NavLinkPropsReactRouter,
  ScrollRestoration as ScrollRestorationReactRouter,
} from './react-router'
import type {PreventAutofocusProp, PreloadProps} from './shared'

type RefProp = {ref?: React.Ref<HTMLAnchorElement>}
type LinkProps = LinkPropsReactRouter & RefProp & PreloadProps
type NavLinkProps = NavLinkPropsReactRouter & RefProp & PreloadProps

/**
 * Export an Outlet based on the router context.
 *
 * TanStack Router's Outlet does not accept props, so we don't support them here.
 * No existing callsites use React Router Outlet props.
 */
export const Outlet = () => {
  const isTanstackContext = useIsTanStackRouter()
  if (isTanstackContext) {
    return <OutletTanStack />
  }
  return <OutletReactRouter />
}

/**
 * `Link` is usually preferred because it preserves GitHub-specific navigation behavior,
 * including `preventAutofocus` support and external route detection.
 *
 * With `SoftNavOnlyLink`, navigation delegates to React Router's raw `Link` instead of checking
 * whether the target is in the app's registered routes; soft navigations to unregistered routes
 * will produce a client-side 404.
 * With `Link`, the same navigation will fall back to a full-page navigation.
 */
export const SoftNavOnlyLink = (props: LinkProps) => {
  const isTanstackContext = useIsTanStackRouter()
  if (isTanstackContext) {
    /** TSR Link always supports PreventAutofocusProp and external route detection */
    return <LinkTanStack {...props} />
  }
  return <SoftNavOnlyLinkReactRouter {...props} />
}

/**
 * `NavLink` is usually preferred because it preserves GitHub-specific navigation behavior,
 * including `preventAutofocus` support and external route detection.
 *
 * With `SoftNavOnlyNavLink`, navigation delegates to React Router's raw `NavLink` instead of
 * checking whether the target is in the app's registered routes; soft navigations to
 * unregistered routes will produce a client-side 404.
 * With `NavLink`, the same navigation will fall back to a full-page navigation.
 */
export const SoftNavOnlyNavLink = (props: NavLinkProps) => {
  const isTanstackContext = useIsTanStackRouter()
  if (isTanstackContext) {
    /** TSR NavLink always supports PreventAutofocusProp and external route detection */
    return <NavLinkTanStack {...props} />
  }
  return <SoftNavOnlyNavLinkReactRouter {...props} />
}

/**
 * A navigation-aware `Link` component that automatically resolves whether
 * to perform a soft navigation, Turbo navigation, or hard navigation based on the target route.
 * Supports `preventAutofocus` to suppress focus restoration after navigation.
 *
 * See [Link docs](?path=/docs/reusable-components-navigation-link--docs) for full props, examples, and accessibility details.
 */
export const Link = (props: LinkProps & PreventAutofocusProp) => {
  const isTanstackContext = useIsTanStackRouter()
  if (isTanstackContext) {
    /** TSR Link always supports PreventAutofocusProp and external route detection */
    return <LinkTanStack {...props} />
  }
  return <LinkReactRouter {...props} />
}

/**
 * A navigation-aware `NavLink` that adds an `active` class when its `to` path matches the current URL.
 * Supports the same preventAutofocus and external-route detection as `Link`.
 *
 * See [Link docs](?path=/docs/reusable-components-navigation-link--docs) for shared props and accessibility details.
 */
export const NavLink = (props: NavLinkProps & PreventAutofocusProp) => {
  const isTanstackContext = useIsTanStackRouter()
  if (isTanstackContext) {
    /** TSR NavLink always supports PreventAutofocusProp and external route detection */
    return <NavLinkTanStack {...props} />
  }
  return <NavLinkReactRouter {...props} />
}

export const ScrollRestoration = (props: ScrollRestorationPropsReactRouter) => {
  const isTanstackContext = useIsTanStackRouter()
  if (isTanstackContext) {
    // TanStack Router scroll restoration is configured at the router level.
    return null
  }
  return <ScrollRestorationReactRouter {...props} />
}
